import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  Clock,
  Play,
  Pause,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Calendar,
  DollarSign,
  Users,
  ArrowRight,
  Loader2,
  History,
  Settings,
  Eye,
  Zap,
} from 'lucide-react';

/**
 * SchedulerDashboard - Admin component for managing the billing scheduler
 *
 * Displays:
 * - Scheduler status (running, paused, next run)
 * - Quick actions (run all, preview, view history)
 * - Task status for each task type
 * - Pending items preview
 */
const SchedulerDashboard = ({ currentUser }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showConfig, setShowConfig] = useState(false);

  // Date range search state
  const [showDateSearch, setShowDateSearch] = useState(false);
  const [dateSearchStart, setDateSearchStart] = useState('');
  const [dateSearchEnd, setDateSearchEnd] = useState('');
  const [scheduledCharges, setScheduledCharges] = useState(null);
  const [dateSearchLoading, setDateSearchLoading] = useState(false);

  // Date simulation state
  const [showSimulation, setShowSimulation] = useState(false);
  const [simulationDate, setSimulationDate] = useState('');
  const [simulationPreview, setSimulationPreview] = useState(null);
  const [simulationLoading, setSimulationLoading] = useState(false);

  // Test execution state
  const [stripeSandboxMode, setStripeSandboxMode] = useState(null);
  const [testExecutionResults, setTestExecutionResults] = useState(null);
  const [showTestConfirm, setShowTestConfirm] = useState(false);

  const isSuperAdmin = currentUser?.role === 'superadmin';
  const hasPermission = isSuperAdmin || currentUser?.admin_permissions?.includes('billing_scheduler');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/status`,
        { headers: getAuthHeaders() }
      );
      setStatus(response.data);
    } catch (err) {
      console.error('Error fetching scheduler status:', err);
      if (err.response?.status === 503) {
        setError('Scheduler service not available. Check server configuration.');
      } else if (err.response?.status !== 403) {
        setError('Failed to fetch scheduler status');
      }
    }
  }, []);

  const fetchPreview = useCallback(async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/preview`,
        { headers: getAuthHeaders() }
      );
      setPreview(response.data);
    } catch (err) {
      console.error('Error fetching preview:', err);
    }
  }, []);

  const fetchStripeMode = useCallback(async () => {
    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/stripe-mode`,
        { headers: getAuthHeaders() }
      );
      setStripeSandboxMode(response.data);
    } catch (err) {
      console.error('Error fetching Stripe mode:', err);
    }
  }, []);

  useEffect(() => {
    if (hasPermission) {
      setLoading(true);
      Promise.all([fetchStatus(), fetchPreview(), fetchStripeMode()])
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [hasPermission, fetchStatus, fetchPreview, fetchStripeMode]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!hasPermission) return;

    const interval = setInterval(() => {
      fetchStatus();
      fetchPreview();
    }, 30000);

    return () => clearInterval(interval);
  }, [hasPermission, fetchStatus, fetchPreview]);

  const handlePauseResume = async () => {
    setError('');
    setSuccess('');
    setActionLoading(true);

    try {
      const endpoint = status?.config?.paused ? 'resume' : 'pause';
      await axios.post(
        `${getApiBase()}/admin/scheduler/${endpoint}`,
        {},
        { headers: getAuthHeaders() }
      );
      setSuccess(status?.config?.paused ? 'Scheduler resumed' : 'Scheduler paused');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTrigger = async (taskType = 'all', dryRun = false) => {
    setError('');
    setSuccess('');
    setActionLoading(true);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/scheduler/trigger`,
        { task_type: taskType, dry_run: dryRun },
        { headers: getAuthHeaders() }
      );
      setSuccess(response.data.message);
      await Promise.all([fetchStatus(), fetchPreview()]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to trigger scheduler');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTriggerSubscription = async (subscriptionId, dryRun = false) => {
    setError('');
    setSuccess('');
    setActionLoading(true);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/scheduler/trigger/${subscriptionId}`,
        { dry_run: dryRun, override_due_check: false },
        { headers: getAuthHeaders() }
      );
      setSuccess(response.data.message);
      await Promise.all([fetchStatus(), fetchPreview()]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to trigger billing');
    } finally {
      setActionLoading(false);
    }
  };

  // Search for scheduled charges in date range
  const handleDateSearch = async () => {
    if (!dateSearchStart || !dateSearchEnd) {
      setError('Please select both start and end dates');
      return;
    }

    setError('');
    setDateSearchLoading(true);

    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/scheduled-charges`,
        {
          params: { start_date: dateSearchStart, end_date: dateSearchEnd },
          headers: getAuthHeaders()
        }
      );
      setScheduledCharges(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch scheduled charges');
    } finally {
      setDateSearchLoading(false);
    }
  };

  // Get preview for a specific date
  const handleGetPreviewForDate = async () => {
    if (!simulationDate) {
      setError('Please select a date');
      return;
    }

    setError('');
    setSimulationLoading(true);

    try {
      const response = await axios.get(
        `${getApiBase()}/admin/scheduler/preview-for-date`,
        {
          params: { as_of_date: simulationDate },
          headers: getAuthHeaders()
        }
      );
      setSimulationPreview(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch preview');
    } finally {
      setSimulationLoading(false);
    }
  };

  // Run simulation for a specific date
  const handleRunSimulation = async () => {
    if (!simulationDate) {
      setError('Please select a date');
      return;
    }

    setError('');
    setSuccess('');
    setSimulationLoading(true);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/scheduler/simulate`,
        {},
        {
          params: { as_of_date: simulationDate },
          headers: getAuthHeaders()
        }
      );
      setSuccess(response.data.message);
      // Refresh the preview to show updated state
      await handleGetPreviewForDate();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to run simulation');
    } finally {
      setSimulationLoading(false);
    }
  };

  // Execute test billing (actual charges in sandbox mode)
  const handleExecuteTest = async () => {
    if (!simulationDate) {
      setError('Please select a date');
      return;
    }

    if (!stripeSandboxMode?.sandbox_mode) {
      setError('Test execution is only available in Stripe sandbox mode');
      return;
    }

    setError('');
    setSuccess('');
    setSimulationLoading(true);
    setShowTestConfirm(false);
    setTestExecutionResults(null);

    try {
      const response = await axios.post(
        `${getApiBase()}/admin/scheduler/execute-test`,
        {},
        {
          params: { as_of_date: simulationDate, confirm_sandbox: true },
          headers: getAuthHeaders()
        }
      );
      setTestExecutionResults(response.data);
      if (response.data.success) {
        setSuccess(response.data.message);
      } else {
        setError(response.data.message);
      }
      // Refresh data after execution
      await Promise.all([fetchStatus(), fetchPreview(), handleGetPreviewForDate()]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to execute test billing');
    } finally {
      setSimulationLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const formatAmount = (cents) => {
    if (!cents) return '$0.00';
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getStatusBadge = (runStatus) => {
    switch (runStatus) {
      case 'completed':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Completed</span>;
      case 'partial':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Partial</span>;
      case 'failed':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">Failed</span>;
      case 'running':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">Running</span>;
      default:
        return null;
    }
  };

  if (!hasPermission) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">
              You don't have permission to access the billing scheduler.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2">Loading scheduler status...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Scheduler</h1>
          <p className="text-gray-600">Manage automatic subscription billing</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/admin/scheduler/history')}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <History className="w-4 h-4" />
            History
          </button>
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Configure
          </button>
          <button
            onClick={handlePauseResume}
            disabled={actionLoading || !status?.config?.enabled}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              status?.config?.paused
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-yellow-600 text-white hover:bg-yellow-700'
            } disabled:opacity-50`}
          >
            {status?.config?.paused ? (
              <>
                <Play className="w-4 h-4" />
                Resume
              </>
            ) : (
              <>
                <Pause className="w-4 h-4" />
                Pause
              </>
            )}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <XCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            &times;
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto text-green-600 hover:text-green-800">
            &times;
          </button>
        </div>
      )}

      {/* Status Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-3 h-3 rounded-full ${
              !status?.config?.enabled
                ? 'bg-gray-400'
                : status?.config?.paused
                ? 'bg-yellow-400'
                : status?.is_running
                ? 'bg-green-400 animate-pulse'
                : 'bg-green-400'
            }`} />
            <div>
              <span className="font-medium text-gray-900">
                Status:{' '}
                {!status?.config?.enabled
                  ? 'Disabled'
                  : status?.config?.paused
                  ? 'Paused'
                  : status?.is_running
                  ? 'Running'
                  : 'Active'}
              </span>
              {status?.current_task && (
                <span className="ml-2 text-sm text-gray-500">
                  (processing: {status.current_task})
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Next Run</div>
            <div className="font-medium">
              {status?.next_scheduled_run ? formatDate(status.next_scheduled_run) : 'Not scheduled'}
            </div>
          </div>
        </div>

        {status?.config?.paused && status?.config?.paused_by_name && (
          <div className="mt-4 p-3 bg-yellow-50 rounded-lg text-sm">
            <span className="text-yellow-800">
              Paused by {status.config.paused_by_name} at {formatDate(status.config.paused_at)}
            </span>
          </div>
        )}

        {status?.config?.dry_run && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800">
              Dry run mode is enabled - actions will be logged but cards won't be charged
            </span>
          </div>
        )}
      </div>

      {/* Configuration Panel */}
      {showConfig && (
        <SchedulerConfigPanel
          config={status?.config}
          onClose={() => setShowConfig(false)}
          onSave={async () => {
            await fetchStatus();
            setShowConfig(false);
          }}
        />
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => handleTrigger('all', false)}
            disabled={actionLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
          >
            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Run All Tasks Now
          </button>
          <button
            onClick={() => handleTrigger('all', true)}
            disabled={actionLoading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
          >
            <Eye className="w-4 h-4" />
            Dry Run (Preview)
          </button>
          <button
            onClick={() => setShowDateSearch(!showDateSearch)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              showDateSearch ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Search Date Range
          </button>
          <button
            onClick={() => setShowSimulation(!showSimulation)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              showSimulation ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Clock className="w-4 h-4" />
            Simulate Date
          </button>
          <button
            onClick={() => {
              fetchStatus();
              fetchPreview();
            }}
            disabled={actionLoading}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Date Range Search Panel */}
      {showDateSearch && (
        <div className="bg-white rounded-lg border border-purple-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-purple-900">Search Scheduled Charges</h2>
            <button
              onClick={() => {
                setShowDateSearch(false);
                setScheduledCharges(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              &times;
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={dateSearchStart}
                onChange={(e) => setDateSearchStart(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={dateSearchEnd}
                onChange={(e) => setDateSearchEnd(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <button
              onClick={handleDateSearch}
              disabled={dateSearchLoading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 disabled:opacity-50"
            >
              {dateSearchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              Search
            </button>
          </div>

          {/* Search Results */}
          {scheduledCharges && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-3 text-sm">
                <span className="text-gray-600">
                  {scheduledCharges.start_date} to {scheduledCharges.end_date}
                </span>
                <span className="font-medium">
                  {scheduledCharges.total_subscriptions} charges totaling {formatAmount(scheduledCharges.total_amount)}
                </span>
              </div>

              {scheduledCharges.by_date?.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {scheduledCharges.by_date.map((dayData) => (
                    <div key={dayData.date} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                        <span className="font-medium">{dayData.date}</span>
                        <span className="text-sm text-gray-600">
                          {dayData.count} charge{dayData.count !== 1 ? 's' : ''} · {formatAmount(dayData.total_amount)}
                        </span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {dayData.subscriptions.map((sub) => (
                          <div key={sub.id} className="px-4 py-2 flex items-center justify-between text-sm">
                            <div>
                              <span className="font-medium">{sub.organization_name}</span>
                              <span className="ml-2 text-gray-500">{sub.package_name}</span>
                              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                {sub.payment_type === 'credit_card' ? '💳 Card' : '📄 Direct Bill'}
                              </span>
                            </div>
                            <span className="font-medium">{formatAmount(sub.effective_price)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No scheduled charges in this date range
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Date Simulation Panel */}
      {showSimulation && (
        <div className="bg-white rounded-lg border border-orange-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-orange-900">Simulate & Test Billing for Date</h2>
            <button
              onClick={() => {
                setShowSimulation(false);
                setSimulationPreview(null);
                setTestExecutionResults(null);
                setShowTestConfirm(false);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              &times;
            </button>
          </div>

          {/* Stripe Mode Indicator */}
          {stripeSandboxMode && (
            <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
              stripeSandboxMode.sandbox_mode
                ? 'bg-yellow-50 border border-yellow-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <AlertCircle className={`w-4 h-4 ${stripeSandboxMode.sandbox_mode ? 'text-yellow-600' : 'text-red-600'}`} />
              <span className={stripeSandboxMode.sandbox_mode ? 'text-yellow-800' : 'text-red-800'}>
                {stripeSandboxMode.sandbox_mode
                  ? 'Stripe Sandbox Mode - Test execution is available'
                  : 'Stripe Production Mode - Test execution is NOT available'
                }
              </span>
            </div>
          )}

          <p className="text-sm text-gray-600 mb-4">
            Preview what would be processed, run a dry-run simulation, or execute actual test billing in sandbox mode.
          </p>

          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={simulationDate}
                onChange={(e) => {
                  setSimulationDate(e.target.value);
                  setSimulationPreview(null);
                  setTestExecutionResults(null);
                  setShowTestConfirm(false);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <button
              onClick={handleGetPreviewForDate}
              disabled={simulationLoading || !simulationDate}
              className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 flex items-center gap-2 disabled:opacity-50"
            >
              {simulationLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
              Preview
            </button>
            <button
              onClick={handleRunSimulation}
              disabled={simulationLoading || !simulationDate}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 disabled:opacity-50"
            >
              {simulationLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Dry Run
            </button>
            {stripeSandboxMode?.sandbox_mode && (
              <>
                {!showTestConfirm ? (
                  <button
                    onClick={() => setShowTestConfirm(true)}
                    disabled={simulationLoading || !simulationDate}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
                    title="Execute actual billing in sandbox mode"
                  >
                    <Zap className="w-4 h-4" />
                    Execute Test
                  </button>
                ) : (
                  <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-sm text-red-700">
                      {simulationPreview?.billing?.count
                        ? `Charge ${simulationPreview.billing.count} subscription(s)?`
                        : 'Process billing for this date?'
                      }
                    </span>
                    <button
                      onClick={handleExecuteTest}
                      disabled={simulationLoading}
                      className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                    >
                      Yes, Execute
                    </button>
                    <button
                      onClick={() => setShowTestConfirm(false)}
                      className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Test Execution Results */}
          {testExecutionResults && (
            <div className="mt-4 mb-4 border-2 border-green-200 rounded-lg overflow-hidden">
              <div className={`px-4 py-3 ${
                testExecutionResults.status === 'completed' ? 'bg-green-100' :
                testExecutionResults.status === 'partial' ? 'bg-yellow-100' : 'bg-red-100'
              }`}>
                <h3 className="font-semibold flex items-center gap-2">
                  {testExecutionResults.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : testExecutionResults.status === 'partial' ? (
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                  Test Execution Results
                </h3>
              </div>
              <div className="p-4">
                {/* Summary */}
                <div className="grid grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <div className="text-2xl font-bold">{testExecutionResults.summary?.total_processed || 0}</div>
                    <div className="text-sm text-gray-500">Processed</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded">
                    <div className="text-2xl font-bold text-green-600">{testExecutionResults.summary?.succeeded || 0}</div>
                    <div className="text-sm text-gray-500">Succeeded</div>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded">
                    <div className="text-2xl font-bold text-red-600">{testExecutionResults.summary?.failed || 0}</div>
                    <div className="text-sm text-gray-500">Failed</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatAmount(testExecutionResults.summary?.total_amount_charged || 0)}
                    </div>
                    <div className="text-sm text-gray-500">Total Charged</div>
                  </div>
                </div>

                {/* Detailed Results */}
                {testExecutionResults.results?.length > 0 && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    <h4 className="font-medium text-sm text-gray-700">Detailed Results:</h4>
                    {testExecutionResults.results.map((result, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded text-sm ${
                          result.success ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {result.success ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500" />
                          )}
                          <div>
                            <span className="font-medium">{result.organization_name}</span>
                            <span className="ml-2 text-gray-500">{result.package_name}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">{formatAmount(result.amount)}</div>
                          {result.success && result.invoice_id && (
                            <div className="text-xs text-gray-500">
                              Invoice: {result.invoice_id.slice(0, 8)}...
                            </div>
                          )}
                          {!result.success && result.error && (
                            <div className="text-xs text-red-600">{result.error}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Errors Summary */}
                {testExecutionResults.errors?.length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded">
                    <h4 className="font-medium text-sm text-red-700 mb-2">Errors:</h4>
                    <ul className="text-sm text-red-600 space-y-1">
                      {testExecutionResults.errors.map((err, idx) => (
                        <li key={idx}>
                          <strong>{err.organization_name}:</strong> {err.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-3 text-xs text-gray-500">
                  Run ID: {testExecutionResults.run_id} | Duration: {testExecutionResults.duration_seconds?.toFixed(2)}s
                </div>
              </div>
            </div>
          )}

          {/* Simulation Preview Results */}
          {simulationPreview && !testExecutionResults && (
            <div className="mt-4 space-y-4">
              <div className="p-3 bg-orange-50 rounded-lg text-sm">
                <span className="font-medium">Simulating as of: </span>
                {new Date(simulationPreview.as_of_date).toLocaleDateString()}
              </div>

              {/* Billing Summary */}
              <div className="border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Billing Charges ({simulationPreview.billing?.count || 0})
                  {simulationPreview.billing?.total_amount > 0 && (
                    <span className="text-sm text-gray-500 font-normal ml-2">
                      Total: {formatAmount(simulationPreview.billing.total_amount)}
                    </span>
                  )}
                </h3>
                {simulationPreview.billing?.items?.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {simulationPreview.billing.items.map((item) => (
                      <div key={item.id} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                        <div>
                          <span className="font-medium">{item.organization_name}</span>
                          <span className="ml-2 text-gray-500">{item.package_name}</span>
                        </div>
                        <span className="font-medium">{formatAmount(item.effective_price)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No billing charges would be processed</p>
                )}
              </div>

              {/* Other summaries */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-gray-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium mb-2">Promo Endings</h4>
                  <p className="text-2xl font-bold">{simulationPreview.promo_endings?.count || 0}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium mb-2">Cancellations</h4>
                  <p className="text-2xl font-bold">{simulationPreview.cancellations?.count || 0}</p>
                </div>
                <div className="border border-gray-200 rounded-lg p-3">
                  <h4 className="text-sm font-medium mb-2">Plan Changes</h4>
                  <p className="text-2xl font-bold">{simulationPreview.scheduled_changes?.count || 0}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Task Status Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <TaskCard
          title="Billing Charges"
          icon={<DollarSign className="w-5 h-5" />}
          lastRun={status?.last_billing_run}
          pendingCount={status?.pending_billing_count || 0}
          onTrigger={() => handleTrigger('billing', false)}
          actionLoading={actionLoading}
        />
        <TaskCard
          title="Promo Endings"
          icon={<Calendar className="w-5 h-5" />}
          lastRun={status?.last_promo_run}
          pendingCount={status?.pending_promo_count || 0}
          onTrigger={() => handleTrigger('promo_endings', false)}
          actionLoading={actionLoading}
        />
        <TaskCard
          title="Cancellations"
          icon={<XCircle className="w-5 h-5" />}
          lastRun={status?.last_cancellation_run}
          pendingCount={status?.pending_cancellation_count || 0}
          onTrigger={() => handleTrigger('cancellations', false)}
          actionLoading={actionLoading}
        />
        <TaskCard
          title="Scheduled Changes"
          icon={<ArrowRight className="w-5 h-5" />}
          lastRun={status?.last_scheduled_changes_run}
          pendingCount={status?.pending_scheduled_changes_count || 0}
          onTrigger={() => handleTrigger('scheduled_changes', false)}
          actionLoading={actionLoading}
        />
      </div>

      {/* Pending Items Preview */}
      {preview && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Pending Items</h2>

          {/* Billing */}
          {preview.billing?.count > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">
                  Due for Billing ({preview.billing.count})
                </h3>
                <span className="text-sm text-gray-500">
                  Total: {formatAmount(preview.billing.total_amount)}
                </span>
              </div>
              <div className="space-y-2">
                {preview.billing.items.slice(0, 5).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <span className="font-medium">{item.organization_name}</span>
                      <span className="ml-2 text-sm text-gray-500">{item.package_name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{formatAmount(item.effective_price)}</span>
                      <button
                        onClick={() => handleTriggerSubscription(item.id, false)}
                        disabled={actionLoading}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        Charge
                      </button>
                    </div>
                  </div>
                ))}
                {preview.billing.count > 5 && (
                  <div className="text-sm text-gray-500 text-center py-2">
                    ... and {preview.billing.count - 5} more
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Promo Endings */}
          {preview.promo_endings?.count > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">
                Promos Ending Today ({preview.promo_endings.count})
              </h3>
              <div className="space-y-2">
                {preview.promo_endings.items.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <span className="font-medium">{item.organization_name}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        {item.promo_type === 'free_trial' ? 'Trial ends' : 'Promo ends'}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      → {formatAmount(item.effective_price)}/
                      {item.billing_interval === 'yearly' ? 'year' : 'month'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cancellations */}
          {preview.cancellations?.count > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-3">
                Pending Cancellations ({preview.cancellations.count})
              </h3>
              <div className="space-y-2">
                {preview.cancellations.items.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <span className="font-medium">{item.organization_name}</span>
                    <span className="text-sm text-red-600">Will be cancelled</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scheduled Changes */}
          {preview.scheduled_changes?.count > 0 && (
            <div>
              <h3 className="font-medium text-gray-900 mb-3">
                Scheduled Plan Changes ({preview.scheduled_changes.count})
              </h3>
              <div className="space-y-2">
                {preview.scheduled_changes.items.slice(0, 3).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <span className="font-medium">{item.organization_name}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        → {item.scheduled_package_name}
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {formatAmount(item.scheduled_effective_price)}/
                      {item.scheduled_billing_interval === 'yearly' ? 'year' : 'month'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {!preview.billing?.count &&
            !preview.promo_endings?.count &&
            !preview.cancellations?.count &&
            !preview.scheduled_changes?.count && (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500" />
                <p>No pending items to process</p>
              </div>
            )}
        </div>
      )}
    </div>
  );
};

/**
 * TaskCard - Individual task status card
 */
const TaskCard = ({ title, icon, lastRun, pendingCount, onTrigger, actionLoading }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'partial':
        return 'text-yellow-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-gray-700">
          {icon}
          <span className="font-medium">{title}</span>
        </div>
        {pendingCount > 0 && (
          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
            {pendingCount} pending
          </span>
        )}
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Last Run:</span>
          <span className={lastRun ? getStatusColor(lastRun.status) : 'text-gray-400'}>
            {lastRun ? formatDate(lastRun.started_at) : 'Never'}
            {lastRun?.status === 'completed' && ' ✓'}
            {lastRun?.status === 'partial' && ' ⚠'}
            {lastRun?.status === 'failed' && ' ✗'}
          </span>
        </div>
        {lastRun && (
          <div className="flex justify-between text-gray-500">
            <span>Processed:</span>
            <span>
              {lastRun.succeeded}/{lastRun.processed}
              {lastRun.failed > 0 && (
                <span className="text-red-500 ml-1">({lastRun.failed} failed)</span>
              )}
            </span>
          </div>
        )}
      </div>

      <button
        onClick={onTrigger}
        disabled={actionLoading || pendingCount === 0}
        className="mt-3 w-full px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Run Now
      </button>
    </div>
  );
};

/**
 * SchedulerConfigPanel - Configuration modal/panel
 */
const SchedulerConfigPanel = ({ config, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    enabled: config?.enabled || false,
    billing_hour: config?.billing_hour || 2,
    billing_minute: config?.billing_minute || 0,
    run_interval: config?.run_interval || 'daily',
    dry_run: config?.dry_run || false,
    pause_on_high_failure: config?.pause_on_high_failure ?? true,
    failure_threshold_percent: config?.failure_threshold_percent || 50,
    tasks_enabled: config?.tasks_enabled || {
      billing: true,
      promo_endings: true,
      cancellations: true,
      scheduled_changes: true,
    },
  });

  const handleSave = async () => {
    setError('');
    setSaving(true);

    try {
      await axios.put(
        `${getApiBase()}/admin/scheduler/config`,
        formData,
        { headers: getAuthHeaders() }
      );
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Scheduler Configuration</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          &times;
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      <div className="space-y-4">
        {/* Enable/Disable */}
        <label className="flex items-center justify-between">
          <span className="font-medium">Scheduler Enabled</span>
          <input
            type="checkbox"
            checked={formData.enabled}
            onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
            className="w-5 h-5 rounded border-gray-300 text-blue-600"
          />
        </label>

        {/* Run Time */}
        <div>
          <label className="block font-medium mb-2">Run Time (UTC)</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="23"
              value={formData.billing_hour}
              onChange={(e) => setFormData({ ...formData, billing_hour: parseInt(e.target.value) })}
              className="w-20 px-3 py-2 border rounded-lg"
            />
            <span>:</span>
            <input
              type="number"
              min="0"
              max="59"
              value={formData.billing_minute}
              onChange={(e) => setFormData({ ...formData, billing_minute: parseInt(e.target.value) })}
              className="w-20 px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        {/* Run Interval */}
        <div>
          <label className="block font-medium mb-2">Run Interval</label>
          <select
            value={formData.run_interval}
            onChange={(e) => setFormData({ ...formData, run_interval: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg"
          >
            <option value="daily">Daily (Production)</option>
            <option value="hourly">Hourly (Testing)</option>
            <option value="15min">Every 15 minutes (Development)</option>
          </select>
        </div>

        {/* Dry Run */}
        <label className="flex items-center justify-between">
          <div>
            <span className="font-medium">Dry Run Mode</span>
            <p className="text-sm text-gray-500">Log actions but don't charge cards</p>
          </div>
          <input
            type="checkbox"
            checked={formData.dry_run}
            onChange={(e) => setFormData({ ...formData, dry_run: e.target.checked })}
            className="w-5 h-5 rounded border-gray-300 text-blue-600"
          />
        </label>

        {/* Task Toggles */}
        <div>
          <label className="block font-medium mb-2">Enabled Tasks</label>
          <div className="space-y-2">
            {[
              { key: 'billing', label: 'Process Billing Charges' },
              { key: 'promo_endings', label: 'Process Promo Endings' },
              { key: 'cancellations', label: 'Process Cancellations' },
              { key: 'scheduled_changes', label: 'Apply Scheduled Changes' },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.tasks_enabled[key]}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tasks_enabled: { ...formData.tasks_enabled, [key]: e.target.checked },
                    })
                  }
                  className="w-4 h-4 rounded border-gray-300 text-blue-600"
                />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Auto-pause on high failure */}
        <label className="flex items-center justify-between">
          <div>
            <span className="font-medium">Pause on High Failure Rate</span>
            <p className="text-sm text-gray-500">
              Auto-pause if &gt;{formData.failure_threshold_percent}% of charges fail
            </p>
          </div>
          <input
            type="checkbox"
            checked={formData.pause_on_high_failure}
            onChange={(e) =>
              setFormData({ ...formData, pause_on_high_failure: e.target.checked })
            }
            className="w-5 h-5 rounded border-gray-300 text-blue-600"
          />
        </label>
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Save Configuration
        </button>
      </div>
    </div>
  );
};

export default SchedulerDashboard;
